import React, { useState, useMemo, useEffect } from "react";
import {
  Container, Row, Col, Card, CardBody, FormGroup, Label,
  Input, Button, Table, Badge, Alert, Nav, NavItem, NavLink,
  InputGroup, InputGroupText
} from "reactstrap";

const SEEGPACalculator = () => {
  const [inputMode, setInputMode] = useState("grade");
  const [scores, setScores] = useState({
    english: "",
    nepali: "",
    science: "",
    mathematics: "",
    social: "",
    computer: "",
    hpe: ""
  });

  const subjects = {
    english: "English",
    nepali: "Nepali",
    science: "Science",
    mathematics: "Mathematics",
    social: "Social Studies",
    computer: "Computer",
    hpe: "Health, Population and Environment"
  };

  const gradeOptions = [
    { value: "", label: "Select Grade" },
    { value: "A+", label: "A+" },
    { value: "A", label: "A" },
    { value: "B+", label: "B+" },
    { value: "B", label: "B" },
    { value: "C+", label: "C+" },
    { value: "C", label: "C" },
    { value: "D+", label: "D+" },
    { value: "D", label: "D" },
    { value: "E", label: "E" },
    { value: "NG", label: "NG" },
  ];

  const marksToGradePoint = (m) => {
    m = parseInt(m);
    if (m >= 90) return 4.0;
    if (m >= 80) return 3.6;
    if (m >= 70) return 3.2;
    if (m >= 60) return 2.8;
    if (m >= 50) return 2.4;
    if (m >= 40) return 2.0;
    if (m >= 35) return 1.6;
    if (m >= 30) return 1.2;
    if (m >= 20) return 0.8;
    return 0.0;
  };

  const gradeToGradePoint = (g) => {
    const map = { 
      "A+": 4.0, "A": 3.6, "B+": 3.2, "B": 2.8, 
      "C+": 2.4, "C": 2.0, "D+": 1.6, "D": 1.2,
      "E": 0.8, "NG": 0.0
    };
    return map[g] || 0;
  };

  const handleInputChange = (field, value) => {
    if (inputMode === "marks" && value !== "" && !/^\d+$/.test(value)) return;
    if (inputMode === "marks" && parseInt(value) > 100) return;
    setScores(prev => ({ ...prev, [field]: value }));
  };

  const handleClearAll = () => {
    setScores({
      english: "",
      nepali: "",
      science: "",
      mathematics: "",
      social: "",
      computer: "",
      hpe: ""
    });
  };

  // Clear scores when switching between marks and grade input modes
  useEffect(() => {
    handleClearAll();
  }, [inputMode]);

  const calculatedGPA = useMemo(() => {
    const validScores = Object.values(scores).filter((s) => s !== "");
    if (validScores.length === 0) return null;
    const totalGP = validScores.reduce((sum, val) => 
      sum + (inputMode === "marks" ? marksToGradePoint(val) : gradeToGradePoint(val)), 0);
    return (totalGP / validScores.length).toFixed(2);
  }, [scores, inputMode]);

  const getGPACategory = (gpa) => {
    if (!gpa) return { text: "Enter grades to calculate", color: "secondary" };
    const gpaNum = parseFloat(gpa);
    if (gpaNum >= 3.6) return { text: "Distinction", color: "success" };
    if (gpaNum >= 3.2) return { text: "First Division", color: "primary" };
    if (gpaNum >= 2.8) return { text: "Second Division", color: "info" };
    if (gpaNum >= 2.0) return { text: "Third Division", color: "warning" };
    return { text: "Need Improvement", color: "danger" };
  };

  const gradeTable = [
    { grade: "A+", points: "4.0", range: "90-100", color: "success" },
    { grade: "A", points: "3.6", range: "80-89", color: "success" },
    { grade: "B+", points: "3.2", range: "70-79", color: "primary" },
    { grade: "B", points: "2.8", range: "60-69", color: "primary" },
    { grade: "C+", points: "2.4", range: "50-59", color: "info" },
    { grade: "C", points: "2.0", range: "40-49", color: "warning" },
    { grade: "D+", points: "1.6", range: "35-39", color: "warning" },
    { grade: "D", points: "1.2", range: "30-34", color: "danger" },
    { grade: "E", points: "0.8", range: "20-29", color: "danger" },
    { grade: "NG", points: "0.0", range: "0-19", color: "dark" },
  ];

  const colors = {
    primary: "#334155",
    textDark: "#0f172a"
  };

  const gpaCategory = getGPACategory(calculatedGPA);
  const percentage = calculatedGPA ? ((parseFloat(calculatedGPA) / 4.0) * 100).toFixed(1) : "0.0";

  return (
    <div style={{ 
      backgroundColor: "#f8f9fa", 
      minHeight: "100vh",
      paddingTop: "2rem",
      paddingBottom: "2rem"
    }}>
      <Container style={{ maxWidth: "900px" }}>
        
        {/* Header Card */}
        <Card className="border-0 shadow-sm mb-4">
          <CardBody className="text-center py-4" style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}>
            <h1 className="text-white mb-2" style={{ fontSize: "2rem", fontWeight: "700" }}>
              🎓 SEE GPA Calculator
            </h1>
            <p className="text-white mb-0" style={{ opacity: 0.9, fontSize: "1rem" }}>
              Calculate your Grade Point Average for SEE Level
            </p>
          </CardBody>
        </Card>

        {/* Input Mode Selection */}
        <Card className="border-0 shadow-sm mb-4">
          <CardBody className="p-4">
            <Label className="small fw-bold text-muted text-uppercase mb-3" style={{ letterSpacing: '1px' }}>
              Input Method
            </Label>
            <Nav pills className="p-1 rounded-pill" style={{ width: 'fit-content', backgroundColor: "#f1f5f9" }}>
              {["grade", "marks"].map((mode) => (
                <NavItem key={mode}>
                  <NavLink
                    className={`rounded-pill px-4 py-2 border-0 fw-bold ${inputMode === mode ? "bg-white shadow-sm" : "text-muted"}`}
                    style={{ 
                      cursor: "pointer", 
                      fontSize: "14px", 
                      color: inputMode === mode ? colors.primary : "#64748b" 
                    }}
                    onClick={() => setInputMode(mode)}
                  >
                    {mode === "marks" ? "Marks Entry" : "Grade Entry"}
                  </NavLink>
                </NavItem>
              ))}
            </Nav>
          </CardBody>
        </Card>

        {/* Grade Input Section */}
        <Card className="border-0 shadow-sm mb-4">
          <CardBody className="p-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <Label className="fw-bold mb-0 text-uppercase" style={{ fontSize: "0.875rem", color: "#6c757d", letterSpacing: "0.5px" }}>
                📝 Enter Your Grades
              </Label>
              <Button 
                color="outline-danger" 
                size="sm"
                onClick={handleClearAll}
              >
                Clear All
              </Button>
            </div>

            <Row className="g-3">
              {Object.keys(subjects).map((subject) => (
                <Col xs="12" md="6" key={subject}>
                  <FormGroup className="mb-0">
                    <Label className="fw-semibold mb-2" style={{ fontSize: "0.9rem" }}>
                      {subjects[subject]}
                    </Label>
                    {inputMode === "marks" ? (
                      <InputGroup className="border rounded-3 overflow-hidden">
                        <Input
                          placeholder="0"
                          className="border-0 bg-white py-2"
                          style={{ boxShadow: "none" }}
                          value={scores[subject]}
                          onChange={(e) => handleInputChange(subject, e.target.value)}
                        />
                        <InputGroupText className="bg-light border-0 text-muted small fw-bold">
                          / 100
                        </InputGroupText>
                      </InputGroup>
                    ) : (
                      <Input
                        type="select"
                        className="form-select"
                        value={scores[subject]}
                        onChange={(e) => handleInputChange(subject, e.target.value)}
                        style={{ 
                          fontSize: "0.95rem",
                          padding: "0.625rem 0.75rem"
                        }}
                      >
                        {gradeOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </Input>
                    )}
                  </FormGroup>
                </Col>
              ))}
            </Row>
          </CardBody>
        </Card>

        {/* Result Card */}
        <Card className="border-0 shadow-sm mb-4">
          <CardBody className="p-4">
            <Label className="fw-bold mb-3 text-uppercase" style={{ fontSize: "0.875rem", color: "#6c757d", letterSpacing: "0.5px" }}>
              📊 Your Results
            </Label>
            
            <Alert 
              color={gpaCategory.color} 
              className="border-0 mb-0"
              style={{ 
                background: calculatedGPA ? undefined : "#e9ecef"
              }}
            >
              <Row className="align-items-center">
                <Col xs="12" md="6" className="text-center text-md-start mb-3 mb-md-0">
                  <div className="text-uppercase fw-semibold mb-2" style={{ fontSize: "0.75rem", opacity: 0.8 }}>
                    Grade Point Average
                  </div>
                  <div style={{ fontSize: "2.5rem", fontWeight: "700", lineHeight: "1" }}>
                    {calculatedGPA || "0.00"}
                  </div>
                  <Badge color={gpaCategory.color} className="mt-2" style={{ fontSize: "0.85rem" }}>
                    {gpaCategory.text}
                  </Badge>
                </Col>
                
                <Col xs="12" md="6">
                  <Row className="g-3">
                    <Col xs="6">
                      <div className="text-center p-3 bg-white rounded">
                        <div className="text-muted mb-1" style={{ fontSize: "0.75rem" }}>Percentage</div>
                        <div className="fw-bold" style={{ fontSize: "1.25rem" }}>{percentage}%</div>
                      </div>
                    </Col>
                    <Col xs="6">
                      <div className="text-center p-3 bg-white rounded">
                        <div className="text-muted mb-1" style={{ fontSize: "0.75rem" }}>Subjects</div>
                        <div className="fw-bold" style={{ fontSize: "1.25rem" }}>
                          {Object.values(scores).filter(s => s !== "").length}/7
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Alert>
          </CardBody>
        </Card>

        {/* Grade Reference Table */}
        <Card className="border-0 shadow-sm">
          <CardBody className="p-4">
            <Label className="fw-bold mb-3 text-uppercase" style={{ fontSize: "0.875rem", color: "#6c757d", letterSpacing: "0.5px" }}>
              📋 Grade Reference Guide
            </Label>
            
            <div className="table-responsive">
              <Table hover className="mb-0 align-middle">
                <thead className="bg-light">
                  <tr>
                    <th className="fw-semibold border-0" style={{ fontSize: "0.875rem" }}>Grade</th>
                    <th className="fw-semibold border-0" style={{ fontSize: "0.875rem" }}>Grade Point</th>
                    <th className="fw-semibold border-0" style={{ fontSize: "0.875rem" }}>Marks Range</th>
                    <th className="fw-semibold border-0" style={{ fontSize: "0.875rem" }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {gradeTable.map((row, idx) => (
                    <tr key={idx}>
                      <td className="fw-bold">{row.grade}</td>
                      <td>{row.points}</td>
                      <td>{row.range}</td>
                      <td>
                        <Badge color={row.color} pill style={{ fontSize: "0.75rem" }}>
                          {parseFloat(row.points) >= 3.6 ? "Excellent" : 
                           parseFloat(row.points) >= 2.8 ? "Good" :
                           parseFloat(row.points) >= 2.0 ? "Average" : "Poor"}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </CardBody>
        </Card>

        {/* Footer */}
        <div className="text-center mt-4">
          <p className="text-muted mb-1" style={{ fontSize: "0.875rem" }}>
            <strong>SEE GPA Calculator</strong> • Grading System for SEE Level
          </p>
          {/* <p className="text-muted mb-0" style={{ fontSize: "0.75rem" }}>
            © 2025 All Rights Reserved
          </p> */}
        </div>

      </Container>
    </div>
  );
};

export default SEEGPACalculator;